import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Headphones, Download, Loader2, Lock } from "lucide-react";

export const ReadingAudio = ({ readingId, unlocked }) => {
  const { t, lang } = useApp();
  const [audioLang, setAudioLang] = useState(lang === "hi" ? "hi" : "en");
  const [status, setStatus] = useState("none");
  const timer = useRef(null);

  useEffect(() => { setAudioLang(lang === "hi" ? "hi" : "en"); }, [lang]);

  const check = async () => {
    try {
      const { data } = await api.get(`/palm/readings/${readingId}/audio`, { params: { lang: audioLang } });
      setStatus(data.status);
      if (data.status === "failed") toast.error(t.result.audioFailed);
      return data.status;
    } catch { return "none"; }
  };

  useEffect(() => {
    let cancelled = false;
    clearInterval(timer.current);
    timer.current = null;
    setStatus("none");
    const start = async () => {
      const s = await check();
      if (cancelled || s !== "processing") return;
      timer.current = setInterval(async () => {
        const st = await check();
        if (st !== "processing") { clearInterval(timer.current); timer.current = null; }
      }, 4000);
    };
    start();
    return () => { cancelled = true; clearInterval(timer.current); };
  }, [audioLang, readingId, unlocked]); // eslint-disable-line

  useEffect(() => {
    if (status !== "processing" || timer.current) return;
    timer.current = setInterval(async () => {
      const s = await check();
      if (s !== "processing") { clearInterval(timer.current); timer.current = null; }
    }, 4000);
  }, [status]); // eslint-disable-line

  const generate = async () => {
    try {
      const { data } = await api.post(`/palm/readings/${readingId}/audio`, null, { params: { lang: audioLang } });
      setStatus(data.status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.result.audioFailed);
    }
  };

  const langLabel = audioLang === "hi" ? t.result.audioLangHi : t.result.audioLangEn;
  const fileUrl = `${API}/palm/readings/${readingId}/audio/file?lang=${audioLang}`;

  return (
    <div className="glass rounded-2xl p-6 mt-10 border border-amber-500/20" data-testid="reading-audio">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-amber-500/10 border border-amber-500/30"><Headphones size={18} className="text-amber-300" /></div>
          <div>
            <h3 className="font-serif text-lg text-amber-100">{t.result.audioTitle}</h3>
            <p className="text-xs text-slate-400">{t.result.audioSub.replace("{lang}", langLabel)}</p>
          </div>
        </div>
        <div className="flex items-center rounded-full border border-amber-500/30 p-0.5 text-xs" data-testid="audio-lang-toggle">
          {["hi", "en"].map((l) => (
            <button key={l} onClick={() => setAudioLang(l)} data-testid={`audio-lang-${l}`}
              className={`px-3 py-1 rounded-full transition-all ${audioLang === l ? "btn-gold" : "text-amber-200/70"}`}>
              {l === "hi" ? t.result.audioLangHi : t.result.audioLangEn}
            </button>
          ))}
        </div>
      </div>

      {!unlocked && (
        <p className="mt-3 text-xs text-amber-200/70 flex items-center gap-1.5" data-testid="audio-preview-note"><Lock size={12} /> {t.result.audioPreviewNote}</p>
      )}

      <div className="mt-4">
        {status === "ready" ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-testid="audio-ready">
            <audio controls preload="none" src={fileUrl} className="w-full sm:flex-1 h-10" data-testid="audio-player" />
            <a href={fileUrl} download data-testid="audio-download-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm flex items-center justify-center gap-2">
              <Download size={16} /> {t.result.audioDownload}
            </a>
          </div>
        ) : status === "processing" ? (
          <div className="flex items-center gap-2 text-sm text-amber-200" data-testid="audio-processing">
            <Loader2 size={16} className="animate-spin" /> {t.result.audioGenerating}
          </div>
        ) : (
          <button onClick={generate} data-testid="audio-generate-btn" className="btn-ghost-gold rounded-full px-6 py-2.5 text-sm flex items-center gap-2 w-full sm:w-auto justify-center">
            <Headphones size={16} /> {t.result.audioGenerate.replace("{lang}", langLabel)}
          </button>
        )}
      </div>
    </div>
  );
};
